import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import {
  findPresetForDrone,
  type DronePreset,
  LICENSE_LEVEL_LABELS,
  CONSTRAINT_LABELS,
  DRONE_TYPE_INFO,
} from "@client/lib/dronePresets";

type DocumentRefJson = {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  objectPath?: string;
  dataBase64?: string;
};

type DroneBattery = {
  id: string;
  name: string;
  serialNumber: string;
  createdAt: string;
};

type WeatherLimits = {
  maxWind: number;
  maxGusts: number;
  maxRain: number;
  minTemp: number;
  maxTemp?: number;
  minCloudCeiling: number;
  minVisibility: number;
};

type Drone = {
  id: string;
  name: string;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  alphaTangoId: string | null;
  classMark: string;
  mtomGrams: number;
  hasCameraOrSensor: boolean;
  hasRemoteId: boolean;
  altitudeLocked120m: boolean;
  charDimensionM: number | null;
  ownedNdFilters: string[] | null;
  batteries: DroneBattery[] | null;
  droneType: string;
  openCategory: string;
  limits: WeatherLimits;
  alphaTangoDocument: DocumentRefJson | null;
  dflightDocument: DocumentRefJson | null;
};

type LogbookEntry = {
  id: string;
  droneName: string;
  flightDate: string;
};

const DASH = "—";

function cleanPresetNotes(notes: string): string {
  return notes
    .replace(/\s*\(JSON[^)]*\)\s*/gi, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+\./g, ".")
    .trim();
}

function fmtDate(iso?: string | null): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE");
}

function or(value: string | number | undefined | null, suffix?: string): string {
  if (value === undefined || value === null || value === "") return DASH;
  return suffix ? `${value} ${suffix}` : String(value);
}

export default function DroneInfo() {
  const { droneId } = useParams<{ droneId: string }>();

  const { data: drones = [], isLoading } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });
  const { data: logbook = [] } = useQuery<LogbookEntry[]>({ queryKey: ["/api/logbook"] });

  const drone = drones.find((d) => d.id === droneId);

  if (isLoading) {
    return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Drohne wird geladen …</div>;
  }

  if (!drone) {
    return (
      <div className="space-y-4">
        <Link to="/drohnen" className="text-sm text-brand-600 hover:underline">
          ← Zurück zu Drohnen
        </Link>
        <div className="card text-center py-12 text-[var(--color-app-text-secondary)]">Drohne nicht gefunden.</div>
      </div>
    );
  }

  const preset: DronePreset | null = findPresetForDrone(
    drone.name,
    drone.manufacturer ?? undefined,
    drone.model ?? undefined,
  );

  const entriesForDrone = logbook.filter((e) => e.droneName === drone.name);
  const sortedEntries = [...entriesForDrone].sort(
    (a, b) => new Date(b.flightDate).getTime() - new Date(a.flightDate).getTime(),
  );
  const lastFlight = sortedEntries[0];

  const classLabel = drone.classMark === "legacy" ? "Bestandsdrohne" : drone.classMark;
  const typeLabel = DRONE_TYPE_INFO[drone.droneType as keyof typeof DRONE_TYPE_INFO]?.label ?? drone.droneType;
  const minLicense = preset ? LICENSE_LEVEL_LABELS[preset.minLicenseLevel] : DASH;
  const presetNotes = preset?.notes ? cleanPresetNotes(preset.notes) : "";

  return (
    <div className="space-y-6" data-testid="screen-drone-info">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link to="/drohnen" className="text-sm text-brand-600 hover:underline">
            ← Zurück zu Drohnen
          </Link>
          <p className="text-lg font-semibold text-[var(--color-app-text)] mt-2">{drone.name}</p>
        </div>
        <Link
          to="/drohnen"
          state={{ editDroneId: drone.id }}
          className="btn-secondary text-sm"
          data-testid="button-edit-drone-info"
        >
          Bearbeiten
        </Link>
      </div>

      <Section title="Stammdaten">
        <Row label="Hersteller" value={or(drone.manufacturer)} />
        <Row label="Modell" value={or(drone.model)} />
        <Row label="EU-Klasse" value={classLabel} />
        <Row label="MTOM" value={`${drone.mtomGrams} g`} />
        <Row label="Seriennummer" value={or(drone.serialNumber)} />
        <Row label="e-ID / AlphaTango" value={or(drone.alphaTangoId)} />
        <Row label="Baujahr" value={or(preset?.year)} />
        <Row label="Typ" value={typeLabel} />
        <Row label="Mindestlizenz" value={minLicense} />
      </Section>

      <Section title="Technische Daten">
        <Row
          label="Remote-ID"
          value={drone.hasRemoteId ? "Ja" : "Nein"}
          valueClass={drone.hasRemoteId ? "text-[var(--color-go)]" : "text-[var(--color-nogo)]"}
        />
        <Row label="120 m-Sperre" value={drone.altitudeLocked120m ? "Ja" : "Nein"} />
        <Row label="Lidar" value={preset?.hasLidar === undefined ? DASH : preset.hasLidar ? "Ja" : "Nein"} />
        <Row label="Kamera / Sensor" value={drone.hasCameraOrSensor ? "Ja" : "Nein"} />
        <Row label="Sensor (Preset)" value={or(preset?.sensor)} />
        <Row
          label="Charakteristische Dimension"
          value={
            drone.charDimensionM && drone.charDimensionM > 0
              ? `${Math.round(drone.charDimensionM * 1000)} mm`
              : DASH
          }
        />
        {preset ? (
          <div className="mt-3 flex items-start gap-2 bg-[var(--color-app-bg-secondary)] rounded-md px-3 py-2">
            <span className="text-[var(--color-app-text-tertiary)] mt-0.5 shrink-0">!</span>
            <p className="text-sm text-[var(--color-app-text-secondary)]">{CONSTRAINT_LABELS[preset.constraintId]}</p>
          </div>
        ) : null}
        {presetNotes ? <p className="text-sm text-[var(--color-app-text-tertiary)] mt-3 leading-relaxed">{presetNotes}</p> : null}
      </Section>

      <Section title="Wetter-Limits">
        <Row label="Max. Wind" value={drone.limits?.maxWind != null ? `${drone.limits.maxWind} m/s` : DASH} />
        <Row label="Max. Böen" value={drone.limits?.maxGusts != null ? `${drone.limits.maxGusts} m/s` : DASH} />
        <Row label="Min. Temperatur" value={drone.limits?.minTemp != null ? `${drone.limits.minTemp} °C` : DASH} />
        <Row label="Max. Temperatur" value={drone.limits?.maxTemp != null ? `${drone.limits.maxTemp} °C` : DASH} />
        <Row label="Max. Regen" value={drone.limits?.maxRain != null ? `${drone.limits.maxRain} mm/h` : DASH} />
        <Row
          label="Min. Wolkenbasis"
          value={drone.limits?.minCloudCeiling != null ? `${drone.limits.minCloudCeiling} m` : DASH}
        />
        <Row
          label="Min. Sichtweite"
          value={drone.limits?.minVisibility != null ? `${drone.limits.minVisibility} m` : DASH}
        />
      </Section>

      <Section title="Flugzähler">
        <Row label="Anzahl Flüge" value={String(entriesForDrone.length)} />
        <Row label="Letzter Flug" value={lastFlight ? fmtDate(lastFlight.flightDate) : DASH} />
      </Section>

      <Section title="Akkus">
        {drone.batteries && drone.batteries.length > 0 ? (
          <ul className="space-y-2">
            {drone.batteries.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/akku/${drone.id}/${b.id}`}
                  className="flex items-center gap-3 bg-[var(--color-app-bg-secondary)] hover:bg-[var(--color-app-bg-tertiary)] rounded-md px-3 py-2"
                  data-testid={`card-battery-${b.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[var(--color-app-text)]">{b.name || "Akku"}</div>
                    <div className="text-xs text-[var(--color-app-text-secondary)] truncate">
                      {b.serialNumber ? `S/N ${b.serialNumber}` : "Keine S/N"}
                    </div>
                  </div>
                  <span className="text-[var(--color-app-text-tertiary)] shrink-0" aria-hidden>›</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[var(--color-app-text-tertiary)] leading-relaxed">
            Keine Akkus hinterlegt. Über „Bearbeiten" können Akkus mit Name und Seriennummer gepflegt werden.
          </p>
        )}
      </Section>

      <Section title="Dokumente">
        {drone.alphaTangoDocument ? (
          <DocRow label="AlphaTango-Bestätigung" doc={drone.alphaTangoDocument} testId={`card-document-alphatango-${drone.id}`} />
        ) : null}
        {drone.dflightDocument ? (
          <DocRow label="D-Flight-Bestätigung" doc={drone.dflightDocument} testId={`card-document-dflight-${drone.id}`} />
        ) : null}
        {preset?.manualUrl ? (
          <a
            href={preset.manualUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 bg-[var(--color-app-bg-secondary)] rounded-md px-3 py-2 hover:bg-[var(--color-app-bg-tertiary)]"
            data-testid={`button-manual-info-${drone.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[var(--color-app-text)]">
                {`${preset.manufacturer} ${preset.model} – Handbuch & Downloads`}
              </div>
              <div className="text-xs text-[var(--color-app-text-secondary)]">Hersteller-Seite öffnen</div>
            </div>
            <span className="text-[var(--color-app-text-tertiary)]">→</span>
          </a>
        ) : null}
        {!drone.alphaTangoDocument && !drone.dflightDocument && !preset?.manualUrl ? (
          <p className="text-sm text-[var(--color-app-text-tertiary)]">Keine Dokumente hinterlegt.</p>
        ) : null}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h2 className="text-xs font-bold uppercase tracking-wide text-brand-700 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm text-[var(--color-app-text-secondary)]">{label}</span>
      <span className={`text-sm font-semibold text-right ${valueClass ?? "text-[var(--color-app-text)]"}`}>{value}</span>
    </div>
  );
}

function DocRow({
  label,
  doc,
  testId,
}: {
  label: string;
  doc: DocumentRefJson;
  testId: string;
}) {
  return (
    <div className="flex items-center gap-3 bg-[var(--color-app-bg-secondary)] rounded-md px-3 py-2 mt-1" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--color-app-text)]">{label}</div>
        <div className="text-xs text-[var(--color-app-text-secondary)] truncate">{doc.fileName}</div>
      </div>
    </div>
  );
}
